<template>
  <section class="map-controls" :aria-label="$t('attention-map-panel.controls')">
    <!--
      A grade das linhas mora aqui dentro, e não na <section>: a barra é o
      container que ela consulta, e a consulta só alcança os descendentes.
    -->
    <div class="map-controls__layout">
      <div class="map-controls__group">
        <div
          v-for="group in groups"
          :key="group.name"
          class="map-controls__set"
          :title="group.label"
        >
          <s-icon class="map-controls__icon" :name="group.icon" />
          <span class="map-controls__label">{{ group.label }}</span>
          <div class="map-controls__chips">
            <button
              type="button"
              class="map-controls__chip"
              :class="{ 'map-controls__chip--on': !group.selected.length }"
              :aria-pressed="String(!group.selected.length)"
              @click="clearGroup(group)"
            >{{ $t('attention-map-panel.filter-all') }}</button>
            <button
              v-for="option in group.options"
              :key="option"
              type="button"
              class="map-controls__chip"
              :class="[
                group.chipClass ? `map-controls__chip--${option}` : '',
                { 'map-controls__chip--on': group.selected.indexOf(option) >= 0 },
              ]"
              :aria-pressed="String(group.selected.indexOf(option) >= 0)"
              @click="toggle(group, option)"
            >{{ $t(`${group.labelPrefix}.${option}`) }}</button>
          </div>
        </div>

        <!--
          Exibição, e não filtro: não muda o que a tela mostra, muda como ela
          desenha. Por isso fica num bloco próprio e o "Limpar" não mexe nele.
        -->
        <div class="map-controls__set" :title="$t('attention-map-panel.filter-display')">
          <s-icon class="map-controls__icon" name="visibility" />
          <span class="map-controls__label">
            {{ $t('attention-map-panel.filter-display') }}
          </span>
          <div class="map-controls__chips">
            <button
              type="button"
              class="map-controls__chip"
              :class="{ 'map-controls__chip--on': showIcons }"
              :aria-pressed="String(showIcons)"
              @click="$emit('update:showIcons', !showIcons)"
            >{{ $t('attention-map-panel.display-icons') }}</button>
            <!--
              Desliga só a contagem de cada fatia do anel do agrupamento. O
              número do centro é o total, e esse nunca se esconde: sem ele o
              agrupamento não diz quantos esconde, que é a razão de existir.
            -->
            <button
              type="button"
              class="map-controls__chip"
              :class="{ 'map-controls__chip--on': showSliceCounts }"
              :aria-pressed="String(showSliceCounts)"
              @click="$emit('update:showSliceCounts', !showSliceCounts)"
            >{{ $t('attention-map-panel.display-slice-counts') }}</button>
          </div>
        </div>

        <div class="map-controls__set map-controls__set--search">
          <s-icon class="map-controls__icon" name="search" />
          <input
            class="map-controls__input"
            type="search"
            :value="query"
            :placeholder="$t('attention-map-panel.search-placeholder')"
            :aria-label="$t('attention-map-panel.search-placeholder')"
            @input="$emit('update:query', $event.target.value)"
          >
          <span v-if="active" class="map-controls__matches">
            {{ $t('attention-map-panel.matches', { count: matchedCount }) }}
          </span>
          <button
            v-if="active"
            type="button"
            class="map-controls__clear"
            @click="$emit('clear')"
          >{{ $t('attention-map-panel.filter-clear') }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  filterableClassifications,
  filterableOrigins,
  isFilterActive,
} from '@/domain/services/generalPanel/attentionMapPanel/attentionMapFilter';

export default {
  name: 'MapControls',
  props: {
    classifications: {
      type: Array,
      default: () => [],
    },
    origins: {
      type: Array,
      default: () => [],
    },
    query: {
      type: String,
      default: '',
    },
    matchedCount: {
      type: Number,
      default: 0,
    },
    showIcons: {
      type: Boolean,
      default: true,
    },
    showSliceCounts: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    groups() {
      return [
        {
          name: 'classifications',
          label: this.$t('attention-map-panel.filter-criticality'),
          labelPrefix: 'attention-map-panel.classification',
          icon: 'exclamation-circle',
          options: filterableClassifications,
          selected: this.classifications,
          // A criticidade escolhida acende na cor da Condição que ela produz.
          chipClass: true,
        },
        {
          name: 'origins',
          label: this.$t('attention-map-panel.filter-origin'),
          labelPrefix: 'attention-map-panel.origin',
          icon: 'bolt',
          options: filterableOrigins,
          selected: this.origins,
        },
      ];
    },
    active() {
      return isFilterActive({
        classifications: this.classifications,
        origins: this.origins,
        query: this.query,
      });
    },
  },
  methods: {
    /**
     * Cada grupo é uma escolha múltipla, e "Todas" é a ausência de escolha — o
     * mesmo que desmarcar tudo. Emite lista nova em vez de mexer na recebida:
     * o estado mora na URL, e quem manda nele é a página.
     */
    toggle(group, option) {
      const selected = group.selected.indexOf(option) >= 0
        ? group.selected.filter(value => value !== option)
        : group.selected.concat([option]);

      this.$emit(`update:${group.name}`, selected);
    },
    clearGroup(group) {
      this.$emit(`update:${group.name}`, []);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

// Rótulo fora da tela, mas ainda lido pelo leitor de tela: sai da vista sem
// deixar o bloco sem nome.
=map-controls-visually-hidden
  border: 0
  clip: rect(0 0 0 0)
  height: 1px
  margin: -1px
  overflow: hidden
  padding: 0
  position: absolute
  white-space: nowrap
  width: 1px

=map-controls-visually-shown
  clip: auto
  height: auto
  margin: 0
  overflow: visible
  position: static
  width: auto

// Mesmo desenho da Barra de Controles do Painel de Status de Comunicação, e os
// mesmos tamanhos: controle é controle nos dois painéis. Tamanho fixo de
// propósito — no painel-guia a barra não cresce com a tela, e foi o crescimento
// dela aqui que deixou os dois lados desproporcionais.
//
// Os limites medem o conteúdo da própria barra, não a janela: o menu lateral
// tira largura dela sem a janela mudar. Estimados sem filtro ativo; com filtro,
// a contagem e o "Limpar" cabem porque a busca encolhe até 4rem.
// Linha única sem os rótulos: quatro blocos só com ícone e opções mais a busca.
$single-line-min: 74rem
// Linha única com os rótulos em caixa alta e a busca na largura cheia.
$labels-min: 96rem

.map-controls
  background: var(--panel-surface-raised)
  border-bottom: 1px solid var(--panel-border)
  container-name: map-controls
  container-type: inline-size
  padding: 0.5rem 1.2rem

  // Padrão: duas linhas — em cima o que recorta, embaixo a busca. É também o
  // que vê o navegador que não sabe medir a barra (TV com navegador embarcado
  // antigo). A linha única é ligada lá embaixo, por @container.
  &__layout
    display: flex
    flex-direction: column
    gap: 0.5rem

  // Quebrar dentro do grupo é o último recurso, para uma janela estreita demais:
  // nunca empurra a página para fora da tela.
  &__group
    align-items: center
    display: flex
    flex-wrap: wrap
    gap: 0.5rem 0.75rem
    min-width: 0

  // Cada conjunto vira um bloco fechado, separado por moldura em vez de espaço
  // solto: com quatro blocos numa linha só, distância sozinha não agrupa.
  &__set
    align-items: center
    background: var(--panel-control-well)
    border: 1px solid var(--panel-border)
    border-radius: 6px
    display: flex
    gap: 0.4rem
    padding: 0.25rem 0.5rem

    // A busca é o único bloco que pode encolher: os outros são opções que
    // precisam aparecer inteiras.
    &--search
      margin-left: auto
      min-width: 0

  &__icon
    color: var(--panel-text-muted)
    font-size: 0.8rem

  // Os rótulos em caixa alta são os primeiros a sair quando a largura aperta:
  // ficam o ícone, com o nome do bloco como dica, e as opções, que se explicam
  // sozinhas. Só aparecem com espaço de sobra.
  &__label
    color: var(--panel-text-muted)
    font-size: 0.66rem
    letter-spacing: 0.07em
    text-transform: uppercase
    +map-controls-visually-hidden

  &__chips
    display: flex
    gap: 0.25rem

  &__chip
    background: transparent
    border: 1px solid transparent
    border-radius: 999px
    color: var(--panel-text-muted)
    cursor: pointer
    font-size: 0.75rem
    line-height: 1
    padding: 0.3rem 0.7rem
    transition: background-color 160ms ease-out, color 160ms ease-out
    white-space: nowrap

    &:hover
      background: var(--panel-chip-hover)
      color: var(--panel-text)

    &--on
      background: var(--panel-match)
      color: var(--panel-background)
      font-weight: 700

    // Criticidade ligada veste a cor da Condição que ela produz: escolher
    // "Crítico" acende vermelho, como o card e o marcador correspondentes.
    &--critical.map-controls__chip--on
      background: var(--condition-critical)
      color: var(--condition-ink-critical)

    &--urgent.map-controls__chip--on,
    &--ordinary.map-controls__chip--on
      background: var(--condition-attention)
      color: var(--condition-ink-attention)

  // Encolhe até 4rem quando precisa dar lugar à contagem e ao "Limpar".
  &__input
    background: transparent
    border: none
    color: var(--panel-text)
    flex: 0 1 12rem
    font-size: 0.8rem
    min-width: 4rem
    padding: 0.25rem 0
    width: 12rem

    &::placeholder
      color: var(--panel-text-muted)

    &:focus
      outline: none

  &__matches
    border-left: 1px solid var(--panel-border)
    color: var(--panel-match)
    font-size: 0.74rem
    padding-left: 0.5rem
    white-space: nowrap

  &__clear
    background: transparent
    border: none
    color: var(--panel-match)
    cursor: pointer
    font-size: 0.72rem
    padding: 0
    text-decoration: underline
    white-space: nowrap

    &:hover
      color: var(--panel-text)

  // Seletores `& &__x` (descendentes da raiz) de propósito: o CSS escopado do
  // Vue 2 não carimba o atributo dentro de @container, e sem a raiz na frente a
  // regra perderia por especificidade para a regra base, que é carimbada.

  // Linha única, sem os rótulos: os blocos lado a lado e a busca encostada à
  // direita, mais estreita.
  @container map-controls (min-width: #{$single-line-min})
    & &__layout
      align-items: center
      flex-direction: row
      gap: 0.75rem

    & &__group
      flex: 1 1 auto
      flex-wrap: nowrap

    & &__input
      flex-basis: 9rem
      width: 9rem

  // Com espaço de sobra, voltam os rótulos e a busca na largura cheia.
  @container map-controls (min-width: #{$labels-min})
    & &__label
      +map-controls-visually-shown

    & &__input
      flex-basis: 14rem
      width: 14rem

  // Celular: cada conjunto rola de lado se os chips não couberem, em vez de
  // empurrar a página para fora da tela. Chips mais altos para o dedo; a busca
  // toma a largura inteira. Aqui cada bloco tem linha própria, então o rótulo
  // cabe e volta.
  +media-max-sm()
    padding: 0.5rem 0.8rem

    &__label
      +map-controls-visually-shown

    &__set
      max-width: 100%
      overflow-x: auto

      &--search
        flex: 1 1 100%
        margin-left: 0

    &__chip
      flex: none
      padding: 0.45rem 0.8rem

    &__input
      flex: 1
      min-width: 0
      width: auto
</style>
